





































































































































































































import { Component, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { required, numeric } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCopy, faSync } from '@fortawesome/free-solid-svg-icons';

import { PublishedApp } from '../shared/models';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import Toast from '@/shared/services/toast';
import { govNotifyApi } from './services/govNotifyApi';
import { GovNotifyInstalledApp, GovNotifyTemplate, InstallGovNotifyRequest } from './models';
import GovNotifyServiceConfiguration from './components/GovNotifyServiceConfiguration.vue';
import { GovNotifyServiceViewModel } from './models/govNotifyServiceViewModel';
import AppConfirmModal from '../shared/components/AppConfirmModal.vue';

library.add(faCopy);
library.add(faSync);

@Component({
	components: { GovNotifyServiceConfiguration, AppConfirmModal },
	mixins: [validationMixin],
	validations: {
		apiKey: { required },
		selectedTemplate: { required },
		name: { required },
		phoneNumber: { required, numeric },
	}
})
export default class GovUkNotifySmsConfig extends Vue {
	@Getter public currentApp!: PublishedApp;
	@Getter public currentWorkspace!: WorkspaceViewModel;

	public deletedGovNotifyServices: GovNotifyServiceViewModel[] = [];

	public govNotifyApp: GovNotifyInstalledApp | null = null;
	public messageCallbackUrl: string = govNotifyApi.getMessageWebhookUrl();
	public deliveryCallbackUrl: string = govNotifyApi.getDeliveryWebhookUrl();
	public bearerToken: string = '';

	public isAppInstalled: boolean = false;
	public apiKey: string = '';
	public name: string = '';
	public phoneNumber: string = '';
	public serviceId: string = '';
	public templates: GovNotifyTemplate[] = [];
	public selectedTemplate: GovNotifyTemplate | null = null;

	public services: GovNotifyServiceViewModel[] = [];

	public loadingTemplates: boolean = false;
	public loadingServices: boolean = true;
	public actionInProgress: boolean = false;

	public serviceConfigurationIsInvalid: boolean = true;

	public showConfirmationModal: boolean = false;
	public showDeleteConfirmationModal: boolean = false;
	public hasConfirmedDiscard: boolean = false;
	public appUnsavedState: boolean = false;
	public routerHookFn: any; // A function that will be called to destroy the router beforeLeave hook.

	public async created() {
		this.bindRouterHook();
		if (this.currentApp.isInstalled) {
			try {
				await this.loadApp();
				if (this.hasMultipleGovNotifyServicesEnabled()) {
					await this.loadServices();
				} else if (!this.isAppInstalled && !this.hasMultipleGovNotifyServicesEnabled()) {
					const result = await govNotifyApi.getBearerToken(this.currentWorkspace.id);
					this.serviceId = this.getServiceIdFromBearerToken(result.bearerToken);
					this.bearerToken = result.bearerToken;
				}
			} finally {
				this.$emit('loaded');
			}
		} else {
			this.$emit('loaded');
		}
	}

	public beforeDestroy() {
		this.routerHookFn();
	}

	public bindRouterHook() {
		this.routerHookFn = this.$router.beforeEach((to, from, next) => {
			if (this.appUnsavedState && !this.hasConfirmedDiscard) {
				this.showConfirmationModal = true;
			} else {
				next();
			}
		 });
	}

	public async loadApp() {
		try {
			this.govNotifyApp = await govNotifyApi.getInstalledApp(this.currentWorkspace.id);
			this.isAppInstalled = true;

			if (this.govNotifyApp) {
				this.apiKey = this.govNotifyApp.notifyApiKey;
				this.name = this.govNotifyApp.name;
				this.phoneNumber = this.govNotifyApp.phoneNumber;
			}
		} catch (err) {
			// if it's not found, then we haven't installed the app fully, so let them fill in the form
			if (err.status === 404) {
				this.isAppInstalled = false;
			}
		}
	}

	public async loadServices() {
		this.loadingServices = true;
		try {
			this.services = await govNotifyApi.getServices(this.currentWorkspace.id);
		} catch (err) {
			Toast.showError(err.message);
		} finally {
			this.loadingServices = false;
		}
	}

	public canLoadTemplates() {
		return !this.loadingTemplates && this.apiKey.length > 0;
	}

	public loadTemplates() {
		if (!this.canLoadTemplates()) {
			return;
		}

		this.loadingTemplates = true;

		govNotifyApi.getTemplates(this.currentWorkspace.id, this.apiKey)
			.then(res => {
				this.templates = res.templates;
			})
			.catch(err => Toast.showError(err.message))
			.finally(() => {
				this.loadingTemplates = false;
				if (this.templates.length === 0 && this.$v.selectedTemplate) {
					this.$v.selectedTemplate.$touch();
				}
			});
	}

	public onCopy() {
		Toast.showInfo('Copied to clipboard');
	}

	public hasGovNotifyInstalledApp() {
		return this.govNotifyApp !== null;
	}

	public hasMultipleGovNotifyServicesEnabled() {
		return !!this.currentWorkspace.features.multipleIdentitiesPerChannelEnabled;
	}

	public cancel(force?: boolean) {
		if (this.appUnsavedState && !force) {
			this.showConfirmationModal = true;
			return;
		}
		this.$router.push({ name: 'apps' });
	}

	public handleServiceChange(services: GovNotifyServiceViewModel[]) {
		// Append any deleted services to the array so the user needs to confirm they want to delete them.
		this.deletedGovNotifyServices.push(...this.services.filter(s => !s.isDraft && !services.includes(s)));

		// Set the state of the service the unsaved so the user then needs to confirm they want to discard changes.
		this.appUnsavedState = true;
		this.hasConfirmedDiscard = false;

		// Update the services array
		this.services = services;
	}

	public handleServiceConfigurationError(isInvalid: any) {
		this.serviceConfigurationIsInvalid = isInvalid;
	}

	public submitIsDisabled(): boolean {
		if (this.hasMultipleGovNotifyServicesEnabled()) {
			return this.serviceConfigurationIsInvalid;
		} else {
			return this.$v.$invalid;
		}
	}

	public save(force?: boolean) {
		// If we have non-draft services which have been deleted, show the confirmation modal.
		if (this.deletedGovNotifyServices.length > 0 && !force) {
			this.showDeleteConfirmationModal = true;
			return;
		}
		if (this.actionInProgress) {
			return;
		}
		this.actionInProgress = true;
		if (this.hasMultipleGovNotifyServicesEnabled()) {
			this.saveMultiService();
		} else {
			this.saveSingleService();
		}
	}

	private async saveMultiService() {
		try {
			if (this.isAppInstalled === false) {
				const installRequest = {
					installId: this.currentApp.installedAppId,
					workspaceId: this.currentWorkspace.id
				} as InstallGovNotifyRequest;
				await govNotifyApi.install(installRequest);
			}
			await govNotifyApi.updateServices(this.currentWorkspace.id, this.saveServiceRequest);
			this.appUnsavedState = false;
			this.$emit('configSaved');
		} catch (err) {
			Toast.showError(err.message);
		} finally {
			this.actionInProgress = false;
		}
	}

	private saveSingleService() {
		if (!this.selectedTemplate) {
			return;
		}

		const request = {
			installId: this.currentApp.installedAppId,
			workspaceId: this.currentWorkspace.id,
			notifyApiKey: this.apiKey,
			templateId: this.selectedTemplate.id,
			templateName: this.selectedTemplate.name,
			templateBody: this.selectedTemplate.body,
			serviceId: this.serviceId,
			name: this.name,
			phoneNumber: this.phoneNumber
		} as InstallGovNotifyRequest;

		govNotifyApi.install(request)
			.then(() => {
				this.appUnsavedState = false;
				this.$emit('configSaved');
			})
			.catch((err) => Toast.showError(err.message))
			.finally(() => this.actionInProgress = false);
	}

	private get saveServiceRequest(): GovNotifyServiceViewModel[] {
		return this.services.map((service) => {
			if (!service.isDraft) {
				return service;
			}
			return ({
				id: service.id,
				name: service.name,
				phoneNumber: service.phoneNumber,
				templateId: service.selectedTemplate!.id,
				templateName: service.selectedTemplate!.name,
				templateBody: service.selectedTemplate!.body,
				notifyApiKey: service.notifyApiKey
			});
		});
	}

	private getServiceIdFromBearerToken(bearerToken: string) {
		const claims = JSON.parse(atob(bearerToken.split('.')[1]));
		return claims.serviceId;
	}
}
